//src/services/modelImports.ts
import { api } from "@/lib/apiClient";

export type TemplateItem = {
  id: string;
  name: string;
  is_active: boolean;
  already_exists?: boolean;
};

export type ImportSummary = {
  imported: Record<string, number>;
  skipped: Record<string, number>;
  messages: string[];
};

export async function listDepartmentTemplates(includeInactive = false) {
  return api.get<TemplateItem[]>("/model-imports/departments/templates", {
    params: includeInactive ? { include_inactive: true } : undefined,
  });
}

export async function createDepartmentTemplate(name: string) {
  return api.post<TemplateItem>("/model-imports/departments/templates", { name });
}

export async function updateDepartmentTemplate(id: string, name: string) {
  return api.put<TemplateItem>(`/model-imports/departments/templates/${id}`, { name });
}

export async function setDepartmentTemplateActive(id: string, is_active: boolean) {
  return api.patch<TemplateItem>(`/model-imports/departments/templates/${id}/status`, { is_active });
}

export async function importDepartmentTemplates(templateIds: string[]) {
  return api.post<ImportSummary>("/model-imports/departments/import", { template_ids: templateIds });
}

export async function listJobPositionTemplates(includeInactive = false) {
  return api.get<TemplateItem[]>("/model-imports/job-positions/templates", {
    params: includeInactive ? { include_inactive: true } : undefined,
  });
}

export async function createJobPositionTemplate(name: string) {
  return api.post<TemplateItem>("/model-imports/job-positions/templates", { name });
}

export async function updateJobPositionTemplate(id: string, name: string) {
  return api.put<TemplateItem>(`/model-imports/job-positions/templates/${id}`, { name });
}

export async function setJobPositionTemplateActive(id: string, is_active: boolean) {
  return api.patch<TemplateItem>(`/model-imports/job-positions/templates/${id}/status`, { is_active });
}

export async function importJobPositionTemplates(templateIds: string[]) {
  return api.post<ImportSummary>("/model-imports/job-positions/import", { template_ids: templateIds });
}

export async function importGroModel() {
  return api.post<ImportSummary>("/model-imports/gro/import", {});
}

export type CompanyGroRiskCategory = {
  id: string;
  tenant_id?: string | null;
  code: string;
  name: string;
  description?: string | null;
  risk_theme: string;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type CompanyGroRisk = {
  id: string;
  tenant_id?: string | null;
  risk_category_id: string;
  risk_category_code: string;
  risk_category_name?: string | null;
  risk_theme: string;
  code: string;
  name: string;
  technical_description?: string | null;
  legal_reference?: string | null;
  possible_damage?: string | null;
  example_situations?: string | null;
  control_guidelines?: string | null;
  is_system: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type CompanyGroDimension = {
  id: string;
  tenant_id: string;
  code: string;
  name: string;
  description?: string | null;
  risk_theme: string;
  score_direction: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  questionnaires_count: number;
  questions_count: number;
  active_rules_count: number;
};

export type CompanyGroDimensionRiskRule = {
  id: string;
  tenant_id: string;
  dimension_id: string;
  dimension_code?: string | null;
  dimension_name: string;
  risk_catalog_id: string;
  risk_name: string;
  risk_code?: string | null;
  operator: string;
  threshold_value: number;
  suggested_probability_level_id?: string | null;
  suggested_probability_level_name?: string | null;
  suggested_severity_level_id?: string | null;
  suggested_severity_level_name?: string | null;
  priority: number;
  auto_generate_inventory_draft: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type CompanyGroActionPlanItem = {
  id: string;
  action_plan_model_id: string;
  title: string;
  description?: string | null;
  priority: string;
  sort_order: number;
  default_start_offset_days?: number | null;
  default_due_offset_days?: number | null;
  expected_result?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type CompanyGroActionPlan = {
  id: string;
  tenant_id: string;
  risk_catalog_id?: string | null;
  risk_name?: string | null;
  risk_code?: string | null;
  code: string;
  title: string;
  description?: string | null;
  is_active: boolean;
  created_by?: string | null;
  created_at: string;
  updated_at: string;
  items_count: number;
  linked_risks_count: number;
  items: CompanyGroActionPlanItem[];
};

export type CompanyGroRiskActionLink = {
  id: string;
  risk_catalog_id: string;
  risk_code?: string | null;
  risk_name: string;
  action_plan_model_id: string;
  action_plan_code?: string | null;
  action_plan_title: string;
};

export type CompanyGroRuleActionLink = {
  id: string;
  dimension_risk_rule_id: string;
  dimension_name: string;
  risk_name: string;
  action_plan_model_id: string;
  action_plan_title: string;
  sort_order: number;
  is_default: boolean;
};

export type CompanyGroMatrixLevel = {
  id: string;
  code: string;
  name: string;
  score: number;
};

export type CompanyGroTemplates = {
  risk_categories: CompanyGroRiskCategory[];
  risks: CompanyGroRisk[];
  dimensions: CompanyGroDimension[];
  dimension_risk_rules: CompanyGroDimensionRiskRule[];
  action_plan_models: CompanyGroActionPlan[];
  risk_action_plan_models: CompanyGroRiskActionLink[];
  dimension_risk_rule_action_plan_models: CompanyGroRuleActionLink[];
  probability_levels: CompanyGroMatrixLevel[];
  severity_levels: CompanyGroMatrixLevel[];
  counts: Record<string, number>;
  import_contents: string[];
};

export type CompanyGroRiskInput = {
  risk_category_id: string;
  code: string;
  name: string;
  technical_description?: string | null;
  legal_reference?: string | null;
  possible_damage?: string | null;
  example_situations?: string | null;
  control_guidelines?: string | null;
  is_active: boolean;
};

export type CompanyGroDimensionInput = {
  code: string;
  name: string;
  description?: string | null;
  risk_theme: string;
  score_direction: string;
  is_active: boolean;
};
export type CompanyGroDimensionRiskRuleInput = {
  dimension_id: string;
  risk_catalog_id: string;
  operator: string;
  threshold_value: number;
  suggested_probability_level_id?: string | null;
  suggested_severity_level_id?: string | null;
  priority: number;
  auto_generate_inventory_draft: boolean;
  is_active: boolean;
};
export type CompanyGroActionPlanInput = {
  risk_catalog_id?: string | null;
  code: string;
  title: string;
  description?: string | null;
  is_active: boolean;
};
export type CompanyGroActionPlanItemInput = {
  title: string;
  description?: string | null;
  priority: string;
  sort_order: number;
  default_start_offset_days?: number | null;
  default_due_offset_days?: number | null;
  expected_result?: string | null;
  is_active: boolean;
};

export async function getCompanyGroTemplates() {
  return api.get<CompanyGroTemplates>("/company-gro-templates");
}

export async function createCompanyGroRisk(body: CompanyGroRiskInput) {
  return api.post<CompanyGroRisk>("/company-gro-templates/risks", body);
}

export async function updateCompanyGroRisk(id: string, body: CompanyGroRiskInput) {
  return api.put<CompanyGroRisk>(`/company-gro-templates/risks/${id}`, body);
}

export async function setCompanyGroRiskStatus(id: string, is_active: boolean) {
  return api.patch<CompanyGroRisk>(`/company-gro-templates/risks/${id}/status`, { is_active });
}

export async function createCompanyGroDimension(body: CompanyGroDimensionInput) {
  return api.post<CompanyGroDimension>("/company-gro-templates/dimensions", body);
}

export async function updateCompanyGroDimension(id: string, body: CompanyGroDimensionInput) {
  return api.put<CompanyGroDimension>(`/company-gro-templates/dimensions/${id}`, body);
}

export async function setCompanyGroDimensionStatus(id: string, is_active: boolean) {
  return api.patch<CompanyGroDimension>(`/company-gro-templates/dimensions/${id}/status`, { is_active });
}

export async function createCompanyGroDimensionRiskRule(body: CompanyGroDimensionRiskRuleInput) {
  return api.post<CompanyGroDimensionRiskRule>("/company-gro-templates/dimension-risk-rules", body);
}

export async function updateCompanyGroDimensionRiskRule(id: string, body: CompanyGroDimensionRiskRuleInput) {
  return api.put<CompanyGroDimensionRiskRule>(`/company-gro-templates/dimension-risk-rules/${id}`, body);
}

export async function setCompanyGroDimensionRiskRuleStatus(id: string, is_active: boolean) {
  return api.patch<CompanyGroDimensionRiskRule>(`/company-gro-templates/dimension-risk-rules/${id}/status`, { is_active });
}

export async function createCompanyGroActionPlan(body: CompanyGroActionPlanInput) {
  return api.post<CompanyGroActionPlan>("/company-gro-templates/action-plans", body);
}

export async function updateCompanyGroActionPlan(id: string, body: CompanyGroActionPlanInput) {
  return api.put<CompanyGroActionPlan>(`/company-gro-templates/action-plans/${id}`, body);
}

export async function setCompanyGroActionPlanStatus(id: string, is_active: boolean) {
  return api.patch<CompanyGroActionPlan>(`/company-gro-templates/action-plans/${id}/status`, { is_active });
}

export async function createCompanyGroActionPlanItem(planId: string, body: CompanyGroActionPlanItemInput) {
  return api.post<CompanyGroActionPlanItem>(`/company-gro-templates/action-plans/${planId}/items`, body);
}

export async function updateCompanyGroActionPlanItem(itemId: string, body: CompanyGroActionPlanItemInput) {
  return api.put<CompanyGroActionPlanItem>(`/company-gro-templates/action-plan-items/${itemId}`, body);
}

export async function setCompanyGroActionPlanItemStatus(itemId: string, is_active: boolean) {
  return api.patch<CompanyGroActionPlanItem>(`/company-gro-templates/action-plan-items/${itemId}/status`, { is_active });
}

export async function createCompanyGroRiskActionLink(risk_catalog_id: string, action_plan_model_id: string) {
  return api.post<CompanyGroRiskActionLink>("/company-gro-templates/risk-action-links", {
    risk_catalog_id,
    action_plan_model_id,
  });
}

export async function deleteCompanyGroRiskActionLink(id: string) {
  return api.delete<void>(`/company-gro-templates/risk-action-links/${id}`);
}

export async function createCompanyGroRuleActionLink(
  dimension_risk_rule_id: string,
  action_plan_model_id: string,
  sort_order = 0,
  is_default = false,
) {
  return api.post<CompanyGroRuleActionLink>("/company-gro-templates/rule-action-links", {
    dimension_risk_rule_id,
    action_plan_model_id,
    sort_order,
    is_default,
  });
}

export async function deleteCompanyGroRuleActionLink(id: string) {
  return api.delete<void>(`/company-gro-templates/rule-action-links/${id}`);
}
